/* ============================================================
 * TUNExperience · App pública — acceso al backend
 *
 * El plano público no tiene autenticación: el escaparate, los huecos y la
 * propia reserva son abiertos. Lo que protege la sesión no es un token, sino
 * el pase que se emite al reservar y que la sede valida al llegar el
 * cliente.
 * ============================================================ */

const BASE = import.meta.env.VITE_API_URL || '';

/**
 * Resuelve la dirección de una foto contra el backend.
 *
 * La base de datos guarda rutas relativas ("/fotos/xxx.webp") a propósito:
 * así el día que cambie el dominio no hay que reescribir ninguna fila. Pero
 * el navegador resolvería esa ruta contra el dominio de ESTA aplicación, no
 * contra el de la API, y ahí no hay ninguna imagen.
 *
 * El fallo era además silencioso: al no encontrar el fichero, el servidor de
 * estáticos aplica el fallback de página única y devuelve index.html con un
 * 200, así que el <img> recibe HTML y se ve roto sin ningún 404 en consola.
 *
 * Las direcciones absolutas se dejan tal cual: una sede puede haber añadido
 * una foto alojada fuera.
 */
export const urlFoto = (u) => (u && u.startsWith('/') ? BASE + u : u);

// El plano público no tiene sesión: no hay nada que autenticar. Se reserva
// dando nombre y móvil, y lo que se recibe a cambio es el pase.

export class ErrorApi extends Error {
  constructor(mensaje, estado) {
    super(mensaje);
    this.estado = estado;
  }
}

async function pedir(ruta, { metodo = 'GET', cuerpo } = {}) {
  let res;
  try {
    res = await fetch(BASE + '/api' + ruta, {
      method: metodo,
      headers: cuerpo ? { 'Content-Type': 'application/json' } : {},
      body: cuerpo ? JSON.stringify(cuerpo) : undefined,
    });
  } catch {
    throw new ErrorApi('No hemos podido conectar. Revisa tu conexión.', 0);
  }

  const datos = await res.json().catch(() => null);
  if (!res.ok) throw new ErrorApi(datos?.error || 'Algo ha ido mal. Inténtalo de nuevo.', res.status);
  return datos;
}

export const api = {
  /** Escaparate: sedes activas con fotos, servicios y bonos. Sin identificarse. */
  sedes: () => pedir('/public/sedes'),

  /** Horas del día con cuántos equipos quedan libres. Nunca dice quién ocupa. */
  huecos: (sedeId, fecha) => pedir(`/public/sedes/${sedeId}/huecos?fecha=${fecha}`),

  /** Recupera un pase por su token. El token es la credencial. */
  pase: (token) => pedir(`/public/pases/${token}`),

  /** Cancela la reserva del pase. Hasta 2 horas antes; después, el centro. */
  cancelarPase: (token) => pedir(`/public/pases/${token}/cancelar`, { metodo: 'POST' }),

  /** Comprueba un código promocional para una sede. No consume usos. */
  codigo: (sedeId, codigo) =>
    pedir(`/public/sedes/${sedeId}/codigos/${encodeURIComponent(codigo)}`),

  /**
   * Crea la reserva y devuelve el pase. La confirmación llega al email.
   *
   * ⚠ El teléfono va en el cuerpo y NO está verificado. Es la identidad del
   * cliente en el sistema, pero nada demuestra que el número sea suyo: esa
   * garantía queda para la v2.
   */
  reservar: ({ sedeId, fecha, hora, nombre, telefono, email, codigoPromocional }) =>
    pedir('/public/reservas', {
      metodo: 'POST',
      cuerpo: { sedeId, fecha, hora, nombre, telefono, email,
                ...(codigoPromocional ? { codigoPromocional } : {}) },
    }),
};
